import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import html2pdf from 'html2pdf.js';
import './SchoolDashboard.css';

function AnnualCalendarForm({ asTab = false }) {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  // Year selection
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  
  // Terms data
  const [terms, setTerms] = useState({
    term1: { startDate: '', endDate: '' },
    term2: { startDate: '', endDate: '' },
    term3: { startDate: '', endDate: '' }
  });

  // Events for each term
  const [events, setEvents] = useState({
    term1: [],
    term2: [],
    term3: []
  });

  // Notice days before event - removed from global state, now per event

  // Grades for event selection
  const [grades, setGrades] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [processingYear, setProcessingYear] = useState(null);
  const calendarRef = useRef(null);

  // Generate years for dropdown (current year - 5 to current year + 20 years)
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 20; i++) {
      years.push(i.toString());
    }
    return years;
  };

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      // Reset form data when branch changes
      if (selectedBranchId !== undefined) {
        setTerms({
          term1: { startDate: '', endDate: '' },
          term2: { startDate: '', endDate: '' },
          term3: { startDate: '', endDate: '' }
        });
        setEvents({
          term1: [],
          term2: [],
          term3: []
        });
      }
      fetchGrades();
      fetchCalendarData();
      fetchAcademicYears();
    }
  }, [schoolData?.id, selectedBranchId, selectedYear]);

  const fetchAcademicYears = async () => {
    if (!schoolData?.id) return;

    try {
      let query = supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: false });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;
      if (error) throw error;
      setAcademicYears(data || []);
    } catch (error) {
      console.error('Error fetching academic years:', error);
    }
  };

  const fetchGrades = async () => {
    if (!schoolData?.id) return;

    try {
      let gradesQuery = supabase
        .from('grades')
        .select('*')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        gradesQuery = gradesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: gradesData, error: gradesError } = await gradesQuery.order('name');

      if (gradesError) throw gradesError;
      setGrades(gradesData || []);
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };

  const fetchCalendarData = async () => {
    if (!schoolData?.id || !selectedYear) return;

    setLoadingData(true);
    try {
      // Fetch annual calendar
      let calendarQuery = supabase
        .from('annual_calendars')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('year', selectedYear);

      if (selectedBranchId) {
        calendarQuery = calendarQuery.eq('branch_id', selectedBranchId);
      }

      const { data: calendarData, error: calendarError } = await calendarQuery.single();

      if (calendarError && calendarError.code !== 'PGRST116') {
        throw calendarError;
      }

      if (calendarData) {
        // Fetch terms
        const { data: termsData, error: termsError } = await supabase
          .from('terms')
          .select('*')
          .eq('annual_calendar_id', calendarData.id);

        if (termsError) throw termsError;

        const termsObj = {
          term1: { startDate: '', endDate: '' },
          term2: { startDate: '', endDate: '' },
          term3: { startDate: '', endDate: '' }
        };

        termsData?.forEach(term => {
          if (term.term_number === 1) {
            termsObj.term1 = { startDate: term.start_date || '', endDate: term.end_date || '' };
          } else if (term.term_number === 2) {
            termsObj.term2 = { startDate: term.start_date || '', endDate: term.end_date || '' };
          } else if (term.term_number === 3) {
            termsObj.term3 = { startDate: term.start_date || '', endDate: term.end_date || '' };
          }
        });

        setTerms(termsObj);

        // Fetch events
        const { data: eventsData, error: eventsError } = await supabase
          .from('calendar_events')
          .select('*')
          .eq('annual_calendar_id', calendarData.id)
          .order('start_date');

        if (eventsError) throw eventsError;

        const eventsObj = {
          term1: [],
          term2: [],
          term3: []
        };

        eventsData?.forEach(event => {
          // Parse grade_ids if it's a string
          let gradeIds = [];
          if (event.grade_ids) {
            if (typeof event.grade_ids === 'string') {
              try {
                gradeIds = JSON.parse(event.grade_ids);
              } catch (e) {
                gradeIds = [];
              }
            } else {
              gradeIds = event.grade_ids;
            }
          }

          const eventObj = {
            eventName: event.event_name || event.event_type,
            eventType: event.event_type,
            selectedGrades: gradeIds,
            startDate: event.start_date || '',
            startTime: event.start_time || '',
            endDate: event.end_date || '',
            endTime: event.end_time || '',
            location: event.location || '',
            description: event.description || '',
            noticeDaysBeforeEvent: event.notice_days_before_event || 7
          };

          if (event.term_number === 1) {
            eventsObj.term1.push(eventObj);
          } else if (event.term_number === 2) {
            eventsObj.term2.push(eventObj);
          } else if (event.term_number === 3) {
            eventsObj.term3.push(eventObj);
          }
        });

        setEvents(eventsObj);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setMessage({ type: 'error', text: 'Failed to load calendar data' });
    } finally {
      setLoadingData(false);
    }
  };

  const handleSaveCalendar = async () => {
    if (!schoolData?.id || !selectedYear) {
      setMessage({ type: 'error', text: 'Please select a year' });
      return;
    }

    if (!selectedBranchId) {
      setMessage({ type: 'error', text: 'Please select a branch first before saving the calendar.' });
      return;
    }

    setSaving(true);
    try {
      // Check if calendar exists first
      let calendarQuery = supabase
        .from('annual_calendars')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('year', parseInt(selectedYear));

      if (selectedBranchId) {
        calendarQuery = calendarQuery.eq('branch_id', selectedBranchId);
      } else {
        calendarQuery = calendarQuery.is('branch_id', null);
      }

      const { data: existingCalendar, error: calendarCheckError } = await calendarQuery.maybeSingle();

      if (calendarCheckError && calendarCheckError.code !== 'PGRST116') {
        throw calendarCheckError;
      }

      let calendarId;
      if (existingCalendar) {
        // Update existing calendar (no longer storing notice_days here)
        // Since we're not updating any fields, we can just use the existing ID
        calendarId = existingCalendar.id;
        
        // Only update if there are actual changes to make
        // For now, we'll just use the existing calendar ID
      } else {
        // Insert new calendar (no longer storing notice_days here)
        const { data: calendarData, error: calendarError } = await supabase
          .from('annual_calendars')
          .insert({
            school_id: schoolData.id,
            branch_id: selectedBranchId || null,
            year: parseInt(selectedYear)
          })
          .select()
          .single();

        if (calendarError) throw calendarError;
        calendarId = calendarData.id;
      }

      // Save terms
      for (let termNum = 1; termNum <= 3; termNum++) {
        const termKey = `term${termNum}`;
        const term = terms[termKey];

        // Check if term exists
        const { data: existingTerm, error: termCheckError } = await supabase
          .from('terms')
          .select('id')
          .eq('annual_calendar_id', calendarId)
          .eq('term_number', termNum)
          .maybeSingle();

        if (termCheckError && termCheckError.code !== 'PGRST116') {
          throw termCheckError;
        }

        if (existingTerm) {
          const { error: updateError } = await supabase
            .from('terms')
            .update({
              start_date: term.startDate || null,
              end_date: term.endDate || null
            })
            .eq('id', existingTerm.id);
          
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from('terms')
            .insert({
              annual_calendar_id: calendarId,
              term_number: termNum,
              start_date: term.startDate || null,
              end_date: term.endDate || null
            });
          
          if (insertError) throw insertError;
        }
      }

      // Save events
      for (let termNum = 1; termNum <= 3; termNum++) {
        const termKey = `term${termNum}`;
        const termEvents = events[termKey];

        // Delete existing events for this term
        await supabase
          .from('calendar_events')
          .delete()
          .eq('annual_calendar_id', calendarId)
          .eq('term_number', termNum);

          // Insert new events
          if (termEvents.length > 0) {
            const eventsToInsert = termEvents.map(event => {
              // Validate required fields
              if (!event.startDate || !event.endDate) {
                throw new Error(`Event "${event.eventName || event.eventType || 'Unnamed'}" is missing required dates`);
              }
              
              // Validate event_type is one of the allowed values
              const allowedEventTypes = ['meeting', 'trip', 'public_holiday', 'exam', 'other'];
              const eventType = event.eventType || 'other';
              if (!allowedEventTypes.includes(eventType)) {
                throw new Error(`Invalid event type "${eventType}". Must be one of: ${allowedEventTypes.join(', ')}`);
              }
              
              // Ensure grade_ids is an array (not stringified)
              let gradeIdsArray = [];
              if (event.selectedGrades && event.selectedGrades.length > 0) {
                gradeIdsArray = Array.isArray(event.selectedGrades) 
                  ? event.selectedGrades.filter(g => g !== null && g !== undefined && g !== '')
                  : [event.selectedGrades].filter(g => g !== null && g !== undefined && g !== '');
              }

              return {
                annual_calendar_id: calendarId,
                term_number: termNum,
                event_name: event.eventName || event.eventType || '',
                event_type: eventType,
                grade_ids: gradeIdsArray, // Send as array, Supabase will handle JSONB conversion
                start_date: event.startDate,
                start_time: event.startTime || null,
                end_date: event.endDate,
                end_time: event.endTime || null,
                location: event.location || '',
                description: event.description || '',
                notice_days_before_event: event.noticeDaysBeforeEvent || 7
              };
            });

          const { error: eventsError } = await supabase
            .from('calendar_events')
            .insert(eventsToInsert);

          if (eventsError) throw eventsError;
        }
      }

      // Show beautiful success popup
      setShowSuccessPopup(true);
      setMessage({ type: '', text: '' }); // Clear any existing messages
      await fetchCalendarData();
      
      // Auto-close success popup after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving calendar:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Failed to save calendar';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.details) {
        errorMessage = error.details;
      } else if (error.hint) {
        errorMessage = error.hint;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      setMessage({ 
        type: 'error', 
        text: errorMessage || 'Failed to save calendar. Please check the console for details.' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddEvent = (termKey) => {
    setEvents(prev => ({
      ...prev,
      [termKey]: [...prev[termKey], {
        eventName: '',
        eventType: 'meeting',
        selectedGrades: [],
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        location: '',
        description: '',
        noticeDaysBeforeEvent: 7
      }]
    }));
  };

  const handleEventChange = (termKey, index, field, value) => {
    setEvents(prev => {
      const updated = [...prev[termKey]];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [termKey]: updated };
    });
  };

  const handleRemoveEvent = (termKey, index) => {
    setEvents(prev => ({
      ...prev,
      [termKey]: prev[termKey].filter((_, i) => i !== index)
    }));
  };

  const handleGradeToggle = (termKey, eventIndex, gradeId) => {
    setEvents(prev => {
      const updated = [...prev[termKey]];
      const event = { ...updated[eventIndex] };
      let selectedGrades = [...event.selectedGrades];
      
      // If 'all' is currently selected, clear it first
      if (selectedGrades.includes('all')) {
        selectedGrades = [];
      }
      
      // Toggle the grade
      const gradeIndex = selectedGrades.indexOf(gradeId);
      if (gradeIndex > -1) {
        selectedGrades = selectedGrades.filter(id => id !== gradeId);
      } else {
        selectedGrades = [...selectedGrades, gradeId];
      }
      
      event.selectedGrades = selectedGrades;
      updated[eventIndex] = event;
      
      return { ...prev, [termKey]: updated };
    });
  };

  const handleSelectAllGrades = (termKey, eventIndex) => {
    setEvents(prev => {
      const updated = [...prev[termKey]];
      const event = { ...updated[eventIndex] };
      
      // If 'all' is already selected, deselect it
      if (event.selectedGrades.includes('all')) {
        event.selectedGrades = [];
      } else {
        // Otherwise, select 'all' and clear individual grades
        event.selectedGrades = ['all'];
      }
      
      updated[eventIndex] = event;
      return { ...prev, [termKey]: updated };
    });
  };

  const handleOpenYear = async (yearId) => {
    if (!window.confirm('Are you sure you want to open this academic year? This will import data from the previous year and activate this year.')) {
      return;
    }

    setProcessingYear(yearId);
    try {
      // Find the academic year
      const year = academicYears.find(y => y.id === yearId);
      if (!year) throw new Error('Academic year not found');

      // Find previous year
      const previousYear = academicYears
        .filter(y => y.year < year.year)
        .sort((a, b) => b.year - a.year)[0];

      // Import data from previous year if exists
      if (previousYear) {
        // Import classes
        const { data: prevClasses } = await supabase
          .from('classes')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('academic_year', previousYear.year);

        if (prevClasses && prevClasses.length > 0) {
          const newClasses = prevClasses.map(cls => ({
            ...cls,
            id: undefined,
            academic_year: year.year,
            created_at: new Date().toISOString()
          }));
          await supabase.from('classes').insert(newClasses);
        }

      // Import fees structure (if fee_structures table exists)
      try {
        const { data: prevFees, error: feesError } = await supabase
          .from('fee_structures')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('academic_year', previousYear.year);

        if (!feesError && prevFees && prevFees.length > 0) {
          const newFees = prevFees.map(fee => ({
            ...fee,
            id: undefined,
            academic_year: year.year,
            created_at: new Date().toISOString()
          }));
          await supabase.from('fee_structures').insert(newFees);
        }
      } catch (feesErr) {
        // fee_structures table might not exist, skip it
        console.log('fee_structures table not found, skipping fee import');
      }

      // Also try importing from fee_schedule if it exists
      try {
        const { data: prevFeeSchedule } = await supabase
          .from('fee_schedule')
          .select('*')
          .eq('school_id', schoolData.id);

        if (prevFeeSchedule && prevFeeSchedule.length > 0) {
          // Import fee schedule entries (they don't have academic_year, so we just copy them)
          const newFeeSchedule = prevFeeSchedule.map(fee => ({
            ...fee,
            id: undefined,
            created_at: new Date().toISOString()
          }));
          await supabase.from('fee_schedule').insert(newFeeSchedule);
        }
      } catch (scheduleErr) {
        // fee_schedule table might not exist, skip it
        console.log('fee_schedule table not found, skipping fee schedule import');
      }
      }

      // Activate this year (set is_active = true, deactivate others)
      await supabase
        .from('academic_years')
        .update({ is_active: false })
        .eq('school_id', schoolData.id)
        .neq('id', yearId);

      await supabase
        .from('academic_years')
        .update({ is_active: true, status: 'open', opened_at: new Date().toISOString() })
        .eq('id', yearId);

      setMessage({ type: 'success', text: 'Academic year opened successfully! Data imported from previous year.' });
      await fetchAcademicYears();
    } catch (error) {
      console.error('Error opening year:', error);
      setMessage({ type: 'error', text: 'Failed to open academic year: ' + error.message });
    } finally {
      setProcessingYear(null);
    }
  };

  const handleCloseYear = async (yearId) => {
    if (!window.confirm('Are you sure you want to close this academic year? This will archive all data for this year.')) {
      return;
    }

    setProcessingYear(yearId);
    try {
      // Archive data by updating status
      await supabase
        .from('academic_years')
        .update({ 
          status: 'closed', 
          is_active: false,
          closed_at: new Date().toISOString() 
        })
        .eq('id', yearId);

      // Archive related data (mark as archived)
      const year = academicYears.find(y => y.id === yearId);
      if (year) {
        // Archive classes
        await supabase
          .from('classes')
          .update({ is_archived: true })
          .eq('school_id', schoolData.id)
          .eq('academic_year', year.year);

        // Archive fee records
        await supabase
          .from('fee_records')
          .update({ is_archived: true })
          .eq('school_id', schoolData.id)
          .eq('academic_year', year.year);
      }

      setMessage({ type: 'success', text: 'Academic year closed successfully! All data has been archived.' });
      await fetchAcademicYears();
    } catch (error) {
      console.error('Error closing year:', error);
      setMessage({ type: 'error', text: 'Failed to close academic year: ' + error.message });
    } finally {
      setProcessingYear(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getGradeNames = (gradeIds) => {
    if (!gradeIds || gradeIds.length === 0) return 'None';
    if (gradeIds.includes('all')) return 'All Grades';
    return gradeIds.map(id => {
      const grade = grades.find(g => g.id === id);
      return grade ? grade.name : id;
    }).join(', ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const handleDownloadPDF = async () => {
    if (!calendarRef.current) return;

    // Create a printable version
    const printElement = document.createElement('div');
    printElement.style.padding = '20px';
    printElement.style.fontFamily = 'Arial, sans-serif';
    printElement.style.color = '#1F2937';
    
    let html = `
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="margin: 0; color: #1F2937; font-size: 28px;">Academic Calendar</h1>
        <h2 style="margin: 10px 0; color: #6B7280; font-size: 20px; font-weight: normal;">Year: ${selectedYear || 'N/A'}</h2>
        ${schoolData ? `<p style="margin: 5px 0; color: #6B7280;">${schoolData.name}</p>` : ''}
      </div>
    `;

    // Add terms and events
    [1, 2, 3].forEach(termNum => {
      const termKey = `term${termNum}`;
      const term = terms[termKey];
      const termEvents = events[termKey] || [];

      html += `
        <div style="margin-bottom: 30px; page-break-inside: avoid;">
          <h3 style="background: #F3F4F6; padding: 12px; margin: 0 0 15px 0; border-left: 4px solid #FF8B3D; color: #1F2937;">
            Term ${termNum}
          </h3>
          <div style="padding: 0 12px; margin-bottom: 15px;">
            <p style="margin: 5px 0;"><strong>Start Date:</strong> ${formatDate(term.startDate)}</p>
            <p style="margin: 5px 0;"><strong>End Date:</strong> ${formatDate(term.endDate)}</p>
          </div>
      `;

      if (termEvents.length > 0) {
        html += `<h4 style="margin: 20px 0 10px 0; color: #374151;">Events:</h4>`;
        termEvents.forEach((event, index) => {
          html += `
            <div style="border: 1px solid #E5E7EB; padding: 15px; margin-bottom: 15px; background: #FFFFFF; page-break-inside: avoid;">
              <h5 style="margin: 0 0 10px 0; color: #1F2937; font-size: 16px;">Event ${index + 1}: ${event.eventName || event.eventType}</h5>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 14px;">
                <p style="margin: 5px 0;"><strong>Type:</strong> ${event.eventType}</p>
                <p style="margin: 5px 0;"><strong>Grades:</strong> ${getGradeNames(event.selectedGrades)}</p>
                <p style="margin: 5px 0;"><strong>Start Date:</strong> ${formatDate(event.startDate)}</p>
                <p style="margin: 5px 0;"><strong>Start Time:</strong> ${formatTime(event.startTime) || 'N/A'}</p>
                <p style="margin: 5px 0;"><strong>End Date:</strong> ${formatDate(event.endDate)}</p>
                <p style="margin: 5px 0;"><strong>End Time:</strong> ${formatTime(event.endTime) || 'N/A'}</p>
                ${event.location ? `<p style="margin: 5px 0;"><strong>Location:</strong> ${event.location}</p>` : ''}
                ${event.description ? `<p style="margin: 5px 0; grid-column: 1 / -1;"><strong>Description:</strong> ${event.description}</p>` : ''}
                <p style="margin: 5px 0;"><strong>Notice Days Before:</strong> ${event.noticeDaysBeforeEvent || 7} days</p>
              </div>
            </div>
          `;
        });
      } else {
        html += `<p style="color: #6B7280; padding: 0 12px;">No events scheduled for this term.</p>`;
      }

      html += `</div>`;
    });

    printElement.innerHTML = html;
    document.body.appendChild(printElement);

    const opt = {
      margin: [15, 15, 15, 15],
      filename: `Academic_Calendar_${selectedYear || 'Year'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    try {
      await html2pdf().set(opt).from(printElement).save();
      setMessage({ type: 'success', text: 'Academic calendar downloaded successfully!' });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setMessage({ type: 'error', text: 'Failed to download PDF' });
    } finally {
      document.body.removeChild(printElement);
    }
  };

  if (loading && !asTab) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (loading && asTab) {
    return (
      <div className="dashboard-content">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const formContent = (
    <div className="dashboard-content">
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
        </div>
      )}

      {/* Branch Selector Section */}
      <div className="dashboard-branch-row schedule-view-branch-row" style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
        <BranchSelector
          schoolId={schoolData?.id}
          selectedBranchId={selectedBranchId}
          onBranchChange={handleBranchChange}
        />
        <div className="branch-context" style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6B7280' }}>
          {selectedBranchId 
            ? `📍 Showing annual calendar for the selected branch` 
            : '⚠️ Please select a branch to view and manage annual calendar'}
        </div>
      </div>

      <div className="curriculum-section" ref={calendarRef}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h2 style={{ margin: 0 }}>Annual Calendar Form</h2>
          <div style={{ display: 'flex', gap: '0.75rem' }} className="no-print">
            <button
              type="button"
              onClick={handlePrint}
              className="btn-secondary no-print"
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px 20px',
                background: '#FFFFFF',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                gap: '0.5rem'
              }}
            >
              🖨️ Print
            </button>
            <button
              type="button"
              onClick={handleDownloadPDF}
              className="btn-secondary no-print"
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px 20px',
                background: '#FFFFFF',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                gap: '0.5rem'
              }}
            >
              📥 Download PDF
            </button>
          </div>
        </div>
        
        {/* Academic Year Management Section */}
        <div style={{ 
          background: '#F9FAFB', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          marginBottom: '2rem',
          border: '1px solid #E5E7EB'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#1F2937' }}>📅 Manage Academic Years</h3>
          <p style={{ color: '#6B7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            Open or close academic years. Opening a year imports data from the previous year and activates it. Closing a year archives all data.
          </p>
          
          {academicYears.length === 0 ? (
            <p style={{ color: '#6B7280', textAlign: 'center', padding: '1rem' }}>
              No academic years found. Create one by selecting a year and saving the calendar.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {academicYears.map(year => (
                <div 
                  key={year.id} 
                  style={{ 
                    background: 'white', 
                    padding: '1rem', 
                    borderRadius: '6px', 
                    border: '1px solid #E5E7EB',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <strong style={{ color: '#1F2937' }}>{year.year_name || `Year ${year.year}`}</strong>
                      {year.is_active && (
                        <span style={{ 
                          background: '#10B981', 
                          color: 'white', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          ACTIVE
                        </span>
                      )}
                      {year.status === 'open' && !year.is_active && (
                        <span style={{ 
                          background: '#3B82F6', 
                          color: 'white', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          OPEN
                        </span>
                      )}
                      {year.status === 'closed' && (
                        <span style={{ 
                          background: '#6B7280', 
                          color: 'white', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          CLOSED
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      {year.start_date && year.end_date && (
                        <span>
                          {new Date(year.start_date).toLocaleDateString()} - {new Date(year.end_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {year.status !== 'open' && !year.is_active && (
                      <button
                        onClick={() => handleOpenYear(year.id)}
                        disabled={processingYear === year.id}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#10B981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: processingYear === year.id ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          opacity: processingYear === year.id ? 0.6 : 1
                        }}
                      >
                        {processingYear === year.id ? 'Opening...' : 'Open Year'}
                      </button>
                    )}
                    {year.status !== 'closed' && (
                      <button
                        onClick={() => handleCloseYear(year.id)}
                        disabled={processingYear === year.id}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#EF4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: processingYear === year.id ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          opacity: processingYear === year.id ? 0.6 : 1
                        }}
                      >
                        {processingYear === year.id ? 'Closing...' : 'Close Year'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Show message if no branch is selected */}
        {!selectedBranchId ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem 2rem', 
            background: '#F9FAFB', 
            borderRadius: '8px', 
            border: '1px solid #E5E7EB',
            marginBottom: '2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📍</div>
            <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Select a Branch</h3>
            <p style={{ color: '#6B7280', fontSize: '1.1rem', lineHeight: '1.6' }}>
              Please select a branch from the dropdown above to view and manage the annual calendar for that branch.
            </p>
          </div>
        ) : (
          <>
            {/* Year Selection */}
            <div className="form-group">
              <label>Year *</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="form-input"
              >
                <option value="">-- Select Year --</option>
                {generateYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

        {/* Terms Section */}
        {[1, 2, 3].map(termNum => {
          const termKey = `term${termNum}`;
          const term = terms[termKey];
          const termEvents = events[termKey] || [];

          return (
            <div key={termNum} className="curriculum-section" style={{ marginTop: '24px' }}>
              <h3>Term {termNum}</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label>Start Date (Optional)</label>
                  <input
                    type="date"
                    value={term.startDate}
                    onChange={(e) => setTerms(prev => ({
                      ...prev,
                      [termKey]: { ...prev[termKey], startDate: e.target.value }
                    }))}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>End Date (Optional)</label>
                  <input
                    type="date"
                    value={term.endDate}
                    onChange={(e) => setTerms(prev => ({
                      ...prev,
                      [termKey]: { ...prev[termKey], endDate: e.target.value }
                    }))}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Events for this term */}
              <div style={{ marginTop: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4>Events for Term {termNum}</h4>
                  <button
                    type="button"
                    onClick={() => handleAddEvent(termKey)}
                    style={{ 
                      display: 'inline-block',
                      padding: '4px 8px',
                      background: '#F3F4F6',
                      color: '#374151',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      width: 'fit-content',
                      minWidth: 'fit-content',
                      lineHeight: '1.4',
                      boxSizing: 'border-box'
                    }}
                  >
                    + Add Event
                  </button>
                </div>

                {termEvents.map((event, index) => (
                  <div key={index} className="grade-card" style={{ marginBottom: '16px', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <h5 style={{ margin: 0 }}>Event {index + 1}</h5>
                      <button
                        type="button"
                        onClick={() => handleRemoveEvent(termKey, index)}
                        style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          background: '#EF4444',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          width: 'fit-content',
                          minWidth: 'fit-content',
                          lineHeight: '1.4',
                          boxSizing: 'border-box'
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div className="form-group">
                        <label>Event Name *</label>
                        <select
                          value={event.eventType}
                          onChange={(e) => handleEventChange(termKey, index, 'eventType', e.target.value)}
                          className="form-input"
                        >
                          <option value="meeting">Meeting</option>
                          <option value="trip">Trip</option>
                          <option value="public_holiday">Public Holiday</option>
                          <option value="exam">Exam</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Custom Event Name (if Other)</label>
                        <input
                          type="text"
                          value={event.eventName}
                          onChange={(e) => handleEventChange(termKey, index, 'eventName', e.target.value)}
                          className="form-input"
                          placeholder="Enter event name"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Select Grades *</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        <button
                          type="button"
                          onClick={() => handleSelectAllGrades(termKey, index)}
                          className={event.selectedGrades.includes('all') ? 'btn-primary' : 'btn-secondary'}
                          style={{ 
                            padding: '6px 12px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          All Grades
                        </button>
                        {grades.map(grade => (
                          <button
                            key={grade.id}
                            type="button"
                            onClick={() => handleGradeToggle(termKey, index, grade.id)}
                            className={event.selectedGrades.includes(grade.id) ? 'btn-primary' : 'btn-secondary'}
                            style={{ 
                              padding: '6px 12px',
                              fontSize: '12px',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            {grade.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div className="form-group">
                        <label>Start Date *</label>
                        <input
                          type="date"
                          value={event.startDate}
                          onChange={(e) => handleEventChange(termKey, index, 'startDate', e.target.value)}
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Start Time *</label>
                        <input
                          type="time"
                          value={event.startTime}
                          onChange={(e) => handleEventChange(termKey, index, 'startTime', e.target.value)}
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div className="form-group">
                        <label>End Date *</label>
                        <input
                          type="date"
                          value={event.endDate}
                          onChange={(e) => handleEventChange(termKey, index, 'endDate', e.target.value)}
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>End Time *</label>
                        <input
                          type="time"
                          value={event.endTime}
                          onChange={(e) => handleEventChange(termKey, index, 'endTime', e.target.value)}
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Location</label>
                      <input
                        type="text"
                        value={event.location}
                        onChange={(e) => handleEventChange(termKey, index, 'location', e.target.value)}
                        className="form-input"
                        placeholder="Enter location"
                      />
                    </div>

                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={event.description}
                        onChange={(e) => handleEventChange(termKey, index, 'description', e.target.value)}
                        className="form-input"
                        rows="3"
                        placeholder="Enter event description"
                      />
                    </div>

                    <div className="form-group">
                      <label>Number of Days to Send Notice Before Event *</label>
                      <input
                        type="number"
                        value={event.noticeDaysBeforeEvent || 7}
                        onChange={(e) => handleEventChange(termKey, index, 'noticeDaysBeforeEvent', parseInt(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        placeholder="e.g., 7"
                      />
                      <small style={{ color: '#6B7280', fontSize: '12px' }}>
                        Notices will be sent automatically this many days before this event
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

            {/* Save Button */}
            <div className="form-actions" style={{ marginTop: '32px', display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={handleSaveCalendar}
                className="btn-primary"
                disabled={saving || !selectedYear || !selectedBranchId}
                style={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px 24px',
                  background: '#FF8B3D',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: saving || !selectedYear || !selectedBranchId ? 'not-allowed' : 'pointer',
                  opacity: saving || !selectedYear || !selectedBranchId ? 0.6 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Calendar'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div 
          className="success-popup-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowSuccessPopup(false)}
        >
          <div 
            className="success-popup"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '20px',
              padding: '2.5rem',
              maxWidth: '450px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }}></div>
            
            {/* Success Icon */}
            <div style={{
              position: 'relative',
              zIndex: 1,
              marginBottom: '1.5rem',
              animation: 'scaleIn 0.5s ease-out 0.2s both'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '3px solid white'
              }}>
                <svg 
                  width="50" 
                  height="50" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ animation: 'checkmark 0.6s ease-out 0.4s both' }}
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
            </div>

            {/* Success Message */}
            <h3 style={{
              position: 'relative',
              zIndex: 1,
              color: 'white',
              fontSize: '1.5rem',
              fontWeight: '600',
              margin: '0 0 0.5rem 0',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}>
              Calendar Saved Successfully! 🎉
            </h3>
            
            <p style={{
              position: 'relative',
              zIndex: 1,
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.95rem',
              margin: '0.5rem 0 0 0'
            }}>
              Your annual calendar for {selectedYear} has been saved and is ready to use.
            </p>

            {/* Close Button */}
            <button
              onClick={() => setShowSuccessPopup(false)}
              style={{
                position: 'relative',
                zIndex: 1,
                marginTop: '1.5rem',
                padding: '0.75rem 2rem',
                background: 'white',
                color: '#667eea',
                border: 'none',
                borderRadius: '25px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes checkmark {
          0% {
            stroke-dasharray: 0 50;
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dasharray: 50 0;
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );

  if (asTab) {
    return formContent;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content">
        <div className="content-header desktop-only">
          <h1>Annual Calendar Form</h1>
        </div>
        
        <div className="content-body">
          {formContent}
        </div>
      </div>
    </div>
  );
}

export default AnnualCalendarForm;
